import type { AxiosInstance, AxiosResponse } from "axios";

import { axiosBase, axiosBaseBanner, axiosBaseBucket, axiosBaseHub, axiosBaseToysGate } from "./axios.base";
import { formatRequestConfig } from "./formatRequestConfig";
import type { IRequestConfig } from "./formatRequestConfig";

interface IProps {
	requestUrl?: string;
	baseConfig?: TConfig;
}

type TConfig = "base" | "hub" | "banner" | "bucket" | "toysGate";

type IConfigRoot = {
	base: AxiosInstance;
	hub: AxiosInstance;
	banner: AxiosInstance;
	bucket: AxiosInstance;
	toysGate: AxiosInstance;
};

const configRoot: IConfigRoot = {
	base: axiosBase,
	hub: axiosBaseHub,
	banner: axiosBaseBanner,
	bucket: axiosBaseBucket,
	toysGate: axiosBaseToysGate,
};

export abstract class Controller {
	private readonly _requestUrl: IProps["requestUrl"];

	protected constructor({ requestUrl }: IProps) {
		this._requestUrl = requestUrl;
	}

	protected get<T>(baseConfig: TConfig, path?: string, config?: IRequestConfig) {
		const request = () => configRoot[baseConfig].get<T>(this.processPath(path), formatRequestConfig(config));

		return this.processRequest(request);
	}

	protected post<T>(path?: string, data?: unknown, config?: IRequestConfig, baseConfig: TConfig = "base") {
		const request = () => configRoot[baseConfig].post<T>(this.processPath(path), data, formatRequestConfig(config));

		return this.processRequest(request);
	}

	protected patch<T>(path?: string, data?: unknown, config?: IRequestConfig, baseConfig: TConfig = "base") {
		const request = () => configRoot[baseConfig].patch<T>(this.processPath(path), data, formatRequestConfig(config));

		return this.processRequest(request);
	}

	protected delete<T>(path?: string, config?: IRequestConfig) {
		const request = () => axiosBase.delete<T>(this.processPath(path), formatRequestConfig(config));

		return this.processRequest(request);
	}

	protected put<T>(path?: string, data?: unknown, config?: IRequestConfig) {
		const request = () => axiosBase.put<T>(this.processPath(path), data, formatRequestConfig(config));

		return this.processRequest(request);
	}

	private processRequest<T>(request: () => Promise<AxiosResponse<T>>) {
		if (!this._requestUrl) {
			throw new Error("Expected request url is absent");
		}

		return request();
	}

	private processPath(path?: string) {
		return `${this._requestUrl}${path || ""}`;
	}
}
